import { useMutation } from '@tanstack/react-query';

import type { APIError } from '@/core/lib/fetch';
import Api from '@/core/lib/new-architecture/api';
import Query from '@/core/lib/new-architecture/query';
import getToken from '@/core/lib/new-architecture/query/token';
import Store from '@/core/lib/new-architecture/store';
import type { ReportDonationReasons, ReportReasons, ReportUserReasons } from '@/core/types/report';

type AwaitedReport = Awaited<ReturnType<typeof Api.mutation.report.report>>;

export interface ReportDonation {
  donation: number;
  reasons: ReportDonationReasons[];
  comment?: string;
}

interface ReportDonationCallback {
  onSuccess?: (res: AwaitedReport, data: ReportDonation) => void;
  onError?: (error: APIError) => void;
}

const useReportDonation = ({ onSuccess, onError }: ReportDonationCallback) => {
  const { requestToken } = Store.auth.useRequest();
  const { invalidateDonation } = Query.donation.useInvalidate();

  const mutation = useMutation<AwaitedReport, APIError, ReportDonation>({
    mutationFn: async (data: ReportDonation) => Api.mutation.report.report(data, await getToken(requestToken)),
    onSuccess: async (res, data) => {
      await invalidateDonation(data.donation);
      onSuccess?.(res, data);
    },
    onError,
  });

  const onReport = async (data: ReportDonation) => mutation.mutateAsync(data);

  return { onReport, isLoading: mutation.isPending };
};

export interface ReportUser {
  user: string;
  reasons: ReportUserReasons[];
  comment?: string;
}

interface ReportUserCallback {
  onSuccess?: (res: AwaitedReport, data: ReportUser) => void;
  onError?: (error: APIError) => void;
}

const useReportUser = ({ onSuccess, onError }: ReportUserCallback) => {
  const { requestToken } = Store.auth.useRequest();
  const { invalidate } = Query.user.useInvalidate();

  const mutation = useMutation<AwaitedReport, APIError, ReportUser>({
    mutationFn: async (data: ReportUser) => Api.mutation.report.report(data, await getToken(requestToken)),
    onSuccess: async (res, data) => {
      await invalidate();
      onSuccess?.(res, data);
    },
    onError,
  });

  const onReport = async (data: ReportUser) => mutation.mutateAsync(data);

  return { onReport, isLoading: mutation.isPending };
};

export interface ReportUserDonation {
  donation: number;
  user: string;
  reasons: ReportReasons[];
  comment?: string;
}

interface ReportCallback {
  onSuccess?: (res: AwaitedReport, data: ReportUserDonation) => void;
  onError?: (error: APIError) => void;
}

const useReport = ({ onSuccess, onError }: ReportCallback) => {
  const { requestToken } = Store.auth.useRequest();
  const { invalidate } = Query.user.useInvalidate();

  const mutation = useMutation<AwaitedReport, APIError, ReportUserDonation>({
    mutationFn: async (data: ReportUserDonation) => Api.mutation.report.report(data, await getToken(requestToken)),
    onSuccess: async (res, data) => {
      await invalidate();
      onSuccess?.(res, data);
    },
    onError,
  });

  const onReport = async (data: ReportUserDonation) => mutation.mutateAsync(data);

  return { onReport, isLoading: mutation.isPending };
};

export { useReport, useReportDonation, useReportUser };
